/**
 * @fileoverview Assessment drawer skeleton for self + final, mirroring the
 * AssessmentDrawerBody layout 1:1: project bar (one line), "Skills" and
 * "Standards" sections with score cards (two text lines + 5 hand boxes), the
 * "My notes" textarea and the files section last (same order as the project's
 * Arc assessment drawer). No footer (submit lives in the fixed drawer footer
 * and is hidden while loading).
 */

import { Group, Paper, Skeleton, Stack } from '@mantine/core'
import { MAX_SCORE, MIN_SCORE, SKELETON } from '../../constants'

const { infoBar, assessment } = SKELETON
const scoreLevels = Array.from({ length: MAX_SCORE - MIN_SCORE + 1 }, (_, i) => MIN_SCORE + i)

/** Mirrors AssessmentScoreItem: left-bordered card, category + name lines, picker. */
const ScoreCardSkeleton = () => (
  <Paper withBorder radius="md" p="md" style={{ borderLeftWidth: assessment.cardBorderLeft }}>
    <Stack spacing="sm">
      <Stack spacing={2}>
        <Skeleton height={assessment.itemCategory.height} width={assessment.itemCategory.width} />
        <Skeleton height={assessment.itemName.height} width={assessment.itemName.width} />
      </Stack>
      <Group spacing="xs" noWrap>
        {scoreLevels.map(level => (
          <Skeleton
            key={level}
            height={assessment.scoreBox.size}
            width={assessment.scoreBox.size}
            radius={assessment.scoreBox.radius}
          />
        ))}
      </Group>
    </Stack>
  </Paper>
)

/** Mirrors the body's Section helper: Title order={5} + score cards. */
const SectionSkeleton = ({ rows }: { rows: number }) => (
  <Stack spacing="md">
    <Skeleton height={assessment.sectionTitle.height} width={assessment.sectionTitle.width} />
    {Array.from({ length: rows }, (_, index) => (
      <ScoreCardSkeleton key={index} />
    ))}
  </Stack>
)

export function AssessmentDrawerSkeleton() {
  return (
    <Stack spacing="lg">
      <Paper withBorder radius="sm" p="xs">
        <Skeleton height={infoBar.line.height} width={infoBar.line.width} />
      </Paper>

      <SectionSkeleton rows={assessment.skillRows} />
      <SectionSkeleton rows={assessment.standardRows} />

      <Stack spacing={4}>
        <Skeleton height={assessment.notesLabel.height} width={assessment.notesLabel.width} />
        <Skeleton height={assessment.notesBox.height} radius="sm" />
      </Stack>

      <Stack spacing="sm">
        <Skeleton height={assessment.sectionTitle.height} width={assessment.sectionTitle.width} />
        <Group spacing="xs">
          <Skeleton height={assessment.filesButton.height} width={assessment.filesButton.width} />
          <Skeleton height={assessment.filesButton.height} width={assessment.filesButton.width} />
        </Group>
      </Stack>
    </Stack>
  )
}
